// Libraries
import { useEffect, useState } from 'react';
import Draggable, { DraggableEvent } from 'react-draggable';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Components
import { CountyLegend, TractLegend, EquityLegend, CountySummary } from "./Legend";

// Types
import { EquityIndicator, Indicator, ChangeYear } from '../utils/Types';

//Styles
import styled from "styled-components";

function AccordionStatus({title, defaultExpanded, expanded, setExpanded, children}: 
                         {title: string, defaultExpanded: boolean, expanded?: boolean, setExpanded?: Function, children: React.ReactNode}) {
  
  const [click, updateClick] = useState<number>(0);

  const handleChange = () => {
    const newClick = click + 1;
    updateClick(newClick);

    if (defaultExpanded) {
      setExpanded && setExpanded(newClick % 2 === 0 ? true: false);
    } else {
      setExpanded && setExpanded(newClick % 2 === 0 ? false: true);
    }
    
  };

  return (
    <Accordion defaultExpanded={defaultExpanded} onChange={handleChange}>
      <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="status-content"
          id="status-header"
          className="StatusSummary">
        <h3>{title}</h3>
      </AccordionSummary>
        <AccordionDetails>
          <div className="StatusDetails">
            {children}
          </div>
        </AccordionDetails>
    </Accordion>
  );
}

export const StyledLegendStatus = styled.div.attrs({className: "LegendStatus"})<{ $geofocus: string; }>`
    right: ${props => props.$geofocus === 'US' ? 'auto': '.675rem'};
    left: ${props => props.$geofocus === 'US' ? '.675rem': 'auto'};
`;

function LegendStatus ({geoFocus, title, setLegendExpanded, summaryExpanded, children}: 
                       {geoFocus: string, title: string, setLegendExpanded?: Function, summaryExpanded?: boolean, children: React.ReactNode}) {

  const [legendPositionChange, setLegendPositionChanged] = useState(false);

  const eventHandler = (e: DraggableEvent) => {
    setLegendPositionChanged(true);
  };

  const expanded = summaryExpanded === false && legendPositionChange === false? true: false;

  return (
    <Draggable bounds="body" onDrag={eventHandler}>
       <StyledLegendStatus $geofocus={geoFocus}>
        <AccordionStatus defaultExpanded={true} title={title} setExpanded={setLegendExpanded}>
          <div className="Legend">
              {children}
          </div>
        </AccordionStatus>
      </StyledLegendStatus>
    </Draggable>
  );
}

export function CountyLegendStatus ({geoFocus, equityIndicator, geoHover, pollHover, showEquityIndicator, indicator, setLegendExpanded, summaryExpanded} : 
                                    {geoFocus: string, equityIndicator: EquityIndicator, geoHover: any, pollHover: any,
                                     showEquityIndicator: boolean, indicator: Indicator, setLegendExpanded: Function, summaryExpanded: boolean}) {
  return (
    <LegendStatus geoFocus={geoFocus} setLegendExpanded={setLegendExpanded} summaryExpanded={summaryExpanded} title="Legend" >
        {showEquityIndicator? 
            <EquityLegend equityIndicator={equityIndicator} geoHover={geoHover} geo="State"/>
          : null}
          <CountyLegend pollHover={pollHover} indicator={indicator} showEquityIndicator={showEquityIndicator}/>
    </LegendStatus>
  );
}

export function TractLegendStatus ({ geoFocus, equityIndicator, showPolls, geoHover, pollHover, 
                                    showEquityIndicator, indicator, setLegendExpanded, summaryExpanded, selectedCounty, changeYear} : 
                                   { geoFocus: string, equityIndicator: EquityIndicator,
                                     showPolls: boolean, geoHover: any, pollHover: any, showEquityIndicator: boolean,
                                     indicator: Indicator, setLegendExpanded: Function, summaryExpanded: boolean, selectedCounty: any,
                                     changeYear: ChangeYear}) {
    return (
      <LegendStatus geoFocus={geoFocus} setLegendExpanded={setLegendExpanded} summaryExpanded={summaryExpanded} title="Legend">
        {showEquityIndicator? 
            <EquityLegend equityIndicator={equityIndicator} geoHover={geoHover} geo="County" />
          : null}
        {showPolls ?
          <TractLegend pollHover={pollHover} indicator={indicator} selectedCounty={selectedCounty} changeYear={changeYear} showEquityIndicator={showEquityIndicator}/> 
          : <></>}
       </LegendStatus>
    );
}

const StyledSummaryStatus = styled.div.attrs({className: "SummaryStatus"})<{ $geofocus: string, $animateup: string, $position: string }>`
    // 10 vh is the height of the header
    // 48px is the height of top and bottom margin around the legend

    top: ${props => props.$animateup === 'true' ? 'calc(8vh + 1.35rem + 48px)': `calc(8vh + 1.35rem + 48px + ${props.$position}px)`};
    right: ${props => props.$geofocus === 'US' ? 'auto': '.675rem'};
    left: ${props => props.$geofocus === 'US' ? '.675rem': 'auto'};
    transition: top 200ms 100ms;
`;

function SummaryStatus ({geoFocus, title, legendExpanded, setSummaryExpanded, position, children}: 
                        {geoFocus: string, title: string, legendExpanded: boolean, setSummaryExpanded: Function, position: string, children: React.ReactNode}) {
 
  const [summaryPositionChange, setSummaryPositionChanged] = useState(false);

  const eventHandler = (e: DraggableEvent) => {
    setSummaryPositionChanged(true);
  };

  const animateup = summaryPositionChange === false && legendExpanded === false ? 'true': 'false';
  const expanded = summaryPositionChange === false && legendExpanded === false ? true: false;

  return (
    <Draggable bounds="body" onDrag={eventHandler}>
      <StyledSummaryStatus $geofocus={geoFocus} $animateup={animateup} $position={position}>
        <AccordionStatus defaultExpanded={false} expanded={expanded} setExpanded={setSummaryExpanded} title={title} >
          <div className="Legend">
            {children}
          </div>
        </AccordionStatus>
      </StyledSummaryStatus>
    </Draggable>
  );
}

export function CountySummaryStatus({ geoFocus, legendExpanded, pollHover, indicator, countiesData, loadedCountiesData,
                                      selectedState, selectedCounty, setSummaryExpanded }: 
                                    { geoFocus: string, legendExpanded: boolean, 
                                      pollHover: any, indicator: Indicator, countiesData: GeoJSON.FeatureCollection,
                                      loadedCountiesData: boolean, selectedState: any, selectedCounty: any, setSummaryExpanded: Function }) {
  return(
    <SummaryStatus geoFocus={geoFocus} 
                   legendExpanded={legendExpanded} 
                   setSummaryExpanded={setSummaryExpanded} 
                   title="State Summary" 
                   position='256'>
      <CountySummary pollHover={pollHover} 
                     indicator={indicator} 
                     countiesData={countiesData} 
                     loadedCountiesData={loadedCountiesData}
                     selectedState={selectedState}
                     selectedCounty={selectedCounty}/>
    </SummaryStatus>
  );
} 

export function TractSummaryStatus({ geoFocus, legendExpanded, pollHover, indicator, countiesData, loadedCountiesData,
                                     selectedState, selectedCounty, setSummaryExpanded, showEquityIndicator }: 
                                   { geoFocus: string, legendExpanded: boolean, 
                                     pollHover: any, indicator: Indicator, countiesData: GeoJSON.FeatureCollection,
                                     loadedCountiesData: boolean, selectedState: any, selectedCounty: any, setSummaryExpanded: Function,
                                     showEquityIndicator: boolean }) {
    let position;

    if (selectedCounty.cntyfp === '') {

      if (showEquityIndicator) {
        position = 224;
      } else {
        position = 89;
      }
    } else {

      const pollStatus = selectedCounty.changeYearData.pollSummary.status;
      const adjForMissing = pollStatus?.baseYearNoPollsMissingLatLng > 0 || pollStatus?.lastGEYearNoPollsMissingLatLng > 0;

      if (adjForMissing) {
        if (showEquityIndicator) {
          position = 256;
        } else {
          position = 139;
        }
      } else {
        if (showEquityIndicator) {
          position = 224;
        } else {
          position = 89;
        }
      }
    }
  
    return(
      <SummaryStatus geoFocus={geoFocus} 
                     legendExpanded={legendExpanded} 
                     setSummaryExpanded={setSummaryExpanded} 
                     title="County Summary" 
                     position={position.toString()}>
        <CountySummary pollHover={pollHover} 
                      indicator={indicator} 
                      countiesData={countiesData} 
                      loadedCountiesData={loadedCountiesData}
                      selectedState={selectedState}
                      selectedCounty={selectedCounty}/>
      </SummaryStatus>
    );
}

export default function Status({geoFocus, equityIndicator, showEquityIndicator, indicator, pollHover, geoHover, showPolls, changeYear, 
                                selectedCounty, selectedState, countiesData, loadedCountiesData} : 
                               {geoFocus: string, equityIndicator: EquityIndicator, showEquityIndicator: boolean, indicator: Indicator, 
                                pollHover: any, geoHover: any, showPolls: boolean, changeYear: ChangeYear, selectedCounty: any,
                                selectedState: any, countiesData: GeoJSON.FeatureCollection, loadedCountiesData: boolean}): JSX.Element {

  const [countyLegendExpanded, setCountyLegendExpanded] = useState(true);
  const [countySummaryExpanded, setCountySummaryExpanded] = useState(false);
  const [tractLegendExpanded, setTractLegendExpanded] = useState(true);
  const [tractSummaryExpanded, setTractSummaryExpanded] = useState(false);

  useEffect(() => {
    if (geoFocus === 'State') {
      setTractLegendExpanded(true);
      setTractSummaryExpanded(false);
    } else {
      setCountyLegendExpanded(true);
      setCountySummaryExpanded(false);
    }
  }, [geoFocus]);

  if (geoFocus === 'US') {
    return <></>;
  } else if (geoFocus === "State"){
    return (
      <>
        <CountyLegendStatus
              geoFocus={geoFocus}
              equityIndicator={equityIndicator}
              pollHover={pollHover}
              geoHover={geoHover}
              showEquityIndicator={showEquityIndicator}
              indicator={indicator}
              setLegendExpanded={setCountyLegendExpanded}
              summaryExpanded={countySummaryExpanded}
          />
          <CountySummaryStatus
              geoFocus={geoFocus}
              legendExpanded={countyLegendExpanded}
              setSummaryExpanded={setCountySummaryExpanded}
              pollHover={pollHover}
              indicator={indicator}
              countiesData={countiesData}
              loadedCountiesData={loadedCountiesData}
              selectedCounty={selectedCounty}
              selectedState={selectedState}
          />
      </>);
  } else {
    return (
      <>
        <TractLegendStatus
            geoFocus={geoFocus}
            equityIndicator={equityIndicator}
            showPolls={showPolls}
            pollHover={pollHover}
            geoHover={geoHover}
            showEquityIndicator={showEquityIndicator}
            indicator={indicator}
            setLegendExpanded={setTractLegendExpanded}
            summaryExpanded={tractSummaryExpanded}
            selectedCounty={selectedCounty}
            changeYear={changeYear}
        />
      <TractSummaryStatus
            geoFocus={geoFocus}
            legendExpanded={tractLegendExpanded}
            setSummaryExpanded={setTractSummaryExpanded}
            pollHover={pollHover}
            indicator={indicator}
            countiesData={countiesData}
            loadedCountiesData={loadedCountiesData}
            selectedState={selectedState}
            selectedCounty={selectedCounty}
            showEquityIndicator={showEquityIndicator}
        />
    </>);
  }
}
